import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';

export interface OtpInputProps {
  /**
   * Controlled value: a dense string of digits, never containing holes. Shorter
   * than `length` means the trailing boxes are empty. Keeping it dense is what
   * lets callers just check `value.length === 6` instead of scanning an array.
   */
  value: string;
  onChange: (value: string) => void;
  /** Fires once the last box is filled. */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  /** Accessible name for the group of boxes. */
  label?: string;
  className?: string;
}

const DIGITS_ONLY = /\D/g;

/**
 * Six-box one-time-code field.
 *
 * Behaviour is modelled on WalletFront's `EmailOtpField.jsx`, rewritten in TS
 * with the gaps closed:
 *
 *  - Value is a single controlled STRING, not a `string[]` plus a setter passed
 *    down from the parent. WalletFront's array-of-digits API meant every caller
 *    re-implemented "is it complete yet".
 *  - Paste is handled twice on purpose: `onPaste` (desktop, where we can
 *    `preventDefault`) and the multi-character `onChange` path (Android
 *    keyboards and autofill, which deliver the whole code to one box without
 *    ever firing a paste event). WalletFront had both too — that part was right.
 *  - Arrow keys, Home/End and Delete now work; WalletFront supported only
 *    Backspace.
 *  - Each box has its own accessible name, and the group is a labelled `group`,
 *    so a screen-reader user is told which of six digits they are on.
 *  - The resend countdown is NOT baked in here. WalletFront's field owned a
 *    timer, a resend request and its own error handling, which made it
 *    impossible to reuse for TOTP (no resend exists). Composition instead.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
  error = false,
  autoFocus = false,
  label,
  className
}: OtpInputProps) {
  const { t } = useTranslation();
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const groupId = useId();
  const completedRef = useRef(false);

  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const focusBox = useCallback((index: number) => {
    const clamped = Math.max(0, index);
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select();
  }, []);

  useEffect(() => {
    if (autoFocus) focusBox(0);
  }, [autoFocus, focusBox]);

  // Fire onComplete exactly once per transition into "full", not on every
  // keystroke that leaves it full.
  useEffect(() => {
    const isComplete = value.length === length;
    if (isComplete && !completedRef.current) {
      completedRef.current = true;
      onComplete?.(value);
    } else if (!isComplete) {
      completedRef.current = false;
    }
  }, [value, length, onComplete]);

  /**
   * Writes `incoming` starting at `index` and returns the resulting cursor.
   * The start is clamped to `value.length`, which is what guarantees the string
   * stays dense: you can overwrite an existing digit or append the next one, but
   * you can never punch a hole three boxes ahead of what is filled.
   */
  const writeAt = useCallback(
    (index: number, incoming: string): number => {
      const chars = value.split('');
      let cursor = Math.min(index, value.length);
      for (const char of incoming) {
        if (cursor >= length) break;
        chars[cursor] = char;
        cursor += 1;
      }
      onChange(chars.join('').slice(0, length));
      return cursor;
    },
    [value, length, onChange]
  );

  /** Removes one character, closing the gap — same as a plain text field. */
  const removeAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= value.length) return;
      onChange(value.slice(0, index) + value.slice(index + 1));
    },
    [value, onChange]
  );

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(DIGITS_ONLY, '');

    if (raw === '') {
      removeAt(index);
      return;
    }

    if (raw.length > 1) {
      // Either a paste that bypassed onPaste, or fast typing that landed two
      // characters in one box. Distributing from `index` is correct for both,
      // except when the user is overwriting a single filled box — in that case
      // the last character is the one they meant.
      const isOverwrite = digits[index] !== '' && raw.length === 2;
      const payload = isOverwrite ? (raw.at(-1) ?? '') : raw;
      const cursor = writeAt(index, payload);
      focusBox(Math.min(cursor, length - 1));
      return;
    }

    const cursor = writeAt(index, raw);
    focusBox(Math.min(cursor, length - 1));
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Backspace': {
        event.preventDefault();
        if (index < value.length) {
          // Filled box: clear it in place.
          removeAt(index);
        } else if (index > 0) {
          // Empty box: eat the digit behind and step back, the behaviour every
          // user expects from an OTP field.
          removeAt(index - 1);
          focusBox(index - 1);
        }
        break;
      }
      case 'Delete': {
        event.preventDefault();
        removeAt(index);
        break;
      }
      case 'ArrowLeft':
        event.preventDefault();
        focusBox(index - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        focusBox(Math.min(index + 1, length - 1));
        break;
      case 'Home':
        event.preventDefault();
        focusBox(0);
        break;
      case 'End':
        event.preventDefault();
        focusBox(length - 1);
        break;
      default:
        break;
    }
  };

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(DIGITS_ONLY, '');
    if (!pasted) return;

    event.preventDefault();
    // A full-length paste always fills from the first box, wherever it landed —
    // users paste a whole code into whichever box happens to be focused.
    const start = pasted.length >= length ? 0 : index;
    const cursor = writeAt(start, pasted);
    focusBox(Math.min(cursor, length - 1));
  };

  return (
    <div
      role="group"
      aria-labelledby={`${groupId}-label`}
      aria-invalid={error || undefined}
      className={cn('flex items-center justify-between gap-2', className)}
    >
      <span id={`${groupId}-label`} className="sr-only">
        {label ?? t('otp.label')}
      </span>

      {digits.map((digit, index) => (
        <input
          key={`${groupId}-${String(index)}`}
          ref={(element) => {
            inputsRef.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          // Only the first box advertises one-time-code: repeating it makes iOS
          // offer the autofill chip six times over.
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          // maxLength 1 would silently drop pasted codes on some Android
          // keyboards, so the length is enforced in the handler instead.
          pattern="[0-9]*"
          disabled={disabled}
          value={digit}
          aria-label={t('otp.digit', { index: index + 1, total: length })}
          onChange={(event) => {
            handleChange(index, event);
          }}
          onKeyDown={(event) => {
            handleKeyDown(index, event);
          }}
          onPaste={(event) => {
            handlePaste(index, event);
          }}
          onFocus={(event) => {
            event.target.select();
          }}
          className={cn(
            'h-14 w-full min-w-0 rounded-md border bg-[var(--surface)] text-center',
            'text-title font-semibold text-[var(--text-primary)] tabular-nums',
            'transition-colors duration-150 outline-none',
            'focus:border-[var(--focus-ring)] focus:ring-2 focus:ring-[var(--focus-ring)]/25',
            'disabled:cursor-not-allowed disabled:opacity-60',
            error ? 'border-[var(--danger)]' : 'border-[var(--border)]'
          )}
        />
      ))}
    </div>
  );
}
